import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { TFMService } from 'app/mock-api/tfm.service';
import { Observable, ReplaySubject, catchError, forkJoin, map, of, switchMap, take } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

    /**
     * Constructor
     */
    constructor(private _tfmService: TFMService)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]>
    {
        return this._tfmService.getLoggedInUser().pipe(
            take(1),
            switchMap((user) =>
            {
                const agencyId = user?.agencyId ?? user?.agencyID ?? 0;
                const localAgencySite = this._tfmService.getLocalAgencySite();
                const range = this._buildRangeDays(7);

                const upcomingRequests = [
                    this._buildItinRequests({
                        status: 3,
                        departStart: range.start,
                        departEnd: range.end,
                        type: 'upcoming'
                    }),
                    this._buildItinRequests({
                        status: 4,
                        departStart: range.start,
                        departEnd: range.end,
                        type: 'upcoming'
                    })
                ];

                const returningRequests = [
                    this._buildItinRequests({
                        status: 3,
                        returnStart: range.start,
                        returnEnd: range.end,
                        type: 'returning'
                    }),
                    this._buildItinRequests({
                        status: 4,
                        returnStart: range.start,
                        returnEnd: range.end,
                        type: 'returning'
                    })
                ];

                return forkJoin([...upcomingRequests, ...returningRequests]).pipe(
                    map((responses) =>
                    {
                        const upcomingItins = responses.slice(0, upcomingRequests.length).flat();
                        const returningItins = responses.slice(upcomingRequests.length).flat();

                        const notifications = [
                            ...this._buildNotifications(upcomingItins, 'departure', agencyId, localAgencySite, range),
                            ...this._buildNotifications(returningItins, 'return', agencyId, localAgencySite, range)
                        ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

                        this._notifications.next(notifications);
                        return notifications;
                    })
                );
            }),
            catchError(() =>
            {
                this._notifications.next([]);
                return of([]);
            })
        );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            map((notifications) =>
            {
                const list = notifications ? [...notifications] : [];
                const newNotification = { ...notification };

                this._notifications.next([newNotification, ...list]);
                return newNotification;
            }),
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            map((notifications) =>
            {
                const list = notifications ? [...notifications] : [];
                const index = list.findIndex(item => item.id === id);
                const updatedNotification = {
                    ...(index >= 0 ? list[index] : {}),
                    ...notification
                } as Notification;

                if (index >= 0) {
                    list[index] = updatedNotification;
                } else {
                    list.unshift(updatedNotification);
                }

                this._notifications.next(list);
                return updatedNotification;
            }),
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            map((notifications) =>
            {
                const list = notifications ? [...notifications] : [];
                const index = list.findIndex(item => item.id === id);
                if (index >= 0) {
                    list.splice(index, 1);
                }

                this._notifications.next(list);
                return true;
            }),
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            map((notifications) =>
            {
                const list = notifications ? [...notifications] : [];
                list.forEach((notification) => {
                    notification.read = true;
                });

                this._notifications.next(list);
                return true;
            }),
        );
    }

    private _buildRangeDays(days: number): { start: Date; end: Date }
    {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + days);
        end.setHours(23, 59, 59, 999);

        return { start, end };
    }

    private _buildItinRequests(filter: any): Observable<any[]>
    {
        const payload = {
            length: 200,
            start: 0,
            includeOptions: false,
            includePriceChecks: false,
            type: '',
            ...filter
        };

        return this._tfmService.getItins(0, 0, 0, payload).pipe(
            take(1),
            map((res: any) =>
            {
                const content = res?.content ?? res?.Content ?? res ?? [];
                return Array.isArray(content) ? content : [content];
            }),
            catchError(() => of([]))
        );
    }

    private _buildNotifications(
        itineraries: any[],
        type: 'departure' | 'return',
        agencyId: number,
        localAgencySite: any,
        range: { start: Date; end: Date }
    ): Notification[]
    {
        const notifications: Notification[] = [];

        (itineraries || []).forEach((item) =>
        {
            const itin = item?.itinerary ?? item;
            if (!itin || !this._isSameAgency(itin, agencyId, localAgencySite)) {
                return;
            }

            const eventDateRaw = type === 'departure'
                ? (itin.departDate ?? itin.departureDate ?? itin.startDate)
                : (itin.returnDate ?? itin.returningDate ?? itin.endDate);

            const eventDate = this._parseDate(eventDateRaw);
            if (!eventDate) {
                return;
            }

            if (eventDate < range.start || eventDate > range.end) {
                return;
            }

            const daysUntil = Math.max(0, Math.ceil((eventDate.getTime() - range.start.getTime()) / 86400000));
            const statusName = this._tfmService.getItinStatus(itin.status)?.name || 'Quote';
            const travellerName = this._resolveTravellerName(itin);
            const consultantName = this._resolveConsultantName(itin);
            const reference = itin.reference ?? itin.id ?? itin.guid;
            const whenLabel = daysUntil === 0 ? 'today' : `in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
            const title = type === 'departure'
                ? `Notify consultant: Upcoming departure ${whenLabel}`
                : `Notify consultant: Returning ${whenLabel}`;

            const descriptionParts = [];
            if (travellerName) {
                descriptionParts.push(`<strong>${travellerName}</strong>`);
            }
            if (reference) {
                descriptionParts.push(`${statusName} #${reference}`);
            } else {
                descriptionParts.push(statusName);
            }
            if (consultantName) {
                descriptionParts.push(`Consultant: ${consultantName}`);
            }

            notifications.push({
                id: `${type}-${itin.guid ?? itin.id ?? Math.random().toString(36).slice(2)}`,
                icon: type === 'departure' ? 'heroicons_outline:paper-airplane' : 'heroicons_outline:arrow-uturn-left',
                title,
                description: descriptionParts.join(' · '),
                time: eventDate.toISOString(),
                read: false
            });
        });

        return notifications;
    }

    private _parseDate(value: any): Date | null
    {
        if (!value) {
            return null;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        return date;
    }

    private _resolveTravellerName(itin: any): string
    {
        const name = `${itin?.firstName ?? ''} ${itin?.lastName ?? ''}`.trim();
        if (name) {
            return name;
        }

        return itin?.name || itin?.destination || '';
    }

    private _resolveConsultantName(itin: any): string
    {
        const assigned = itin?.assignedConsultant ?? itin?.assignedAgent ?? itin?.agent ?? {};
        const name = `${assigned?.firstName ?? ''} ${assigned?.lastName ?? ''}`.trim();
        return name;
    }

    private _resolveAgencyId(itin: any): number | null
    {
        const direct = itin?.agencyID ?? itin?.agencyId ?? itin?.agency?.id ?? itin?.agency?.agencyID;
        if (direct) {
            return Number(direct);
        }

        const agentAgency = itin?.agent?.agencyID ?? itin?.agent?.agencyId;
        if (agentAgency) {
            return Number(agentAgency);
        }

        const consultantAgency = itin?.assignedConsultant?.agencyID
            ?? itin?.assignedConsultant?.agencyId
            ?? itin?.assignedAgent?.agencyID
            ?? itin?.assignedAgent?.agencyId;
        if (consultantAgency) {
            return Number(consultantAgency);
        }

        return null;
    }

    private _resolveSiteId(itin: any): number | null
    {
        const siteId = itin?.siteID ?? itin?.siteId ?? itin?.SiteID ?? itin?.SiteId;
        return siteId ? Number(siteId) : null;
    }

    private _isSameAgency(itin: any, agencyId: number, localAgencySite: any): boolean
    {
        if (!agencyId) {
            return true;
        }

        const itinAgencyId = this._resolveAgencyId(itin);
        if (itinAgencyId) {
            return itinAgencyId === agencyId;
        }

        const itinSiteId = this._resolveSiteId(itin);
        if (localAgencySite?.siteID && itinSiteId) {
            return Number(localAgencySite.siteID) === itinSiteId;
        }

        return false;
    }
}
